import React, { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Table, Tag, Button, Typography } from 'antd';
import dayjs from 'dayjs';

const { Title } = Typography;

const STATUS_CONFIG = {
	queued: { color: 'orange', label: 'Processing' },
	completed: { color: 'green', label: 'Completed' },
	failed: { color: 'red', label: 'Failed' }
};

const ScanHistoryPage = ({ scanHistoryPageProps, getScanHistory, resetPage }) => {
	const history = useHistory();
	const intervalRef = useRef(null);
	const { scanJobs, total, page, limit, loading } = scanHistoryPageProps;

	useEffect(() => {
		getScanHistory({ page: 1, limit: 10 });
		return () => {
			resetPage();
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Auto-refresh when there are queued jobs
	useEffect(() => {
		const hasQueuedJobs = scanJobs.some(job => job.status === 'queued');

		if (hasQueuedJobs && !intervalRef.current) {
			intervalRef.current = setInterval(() => {
				getScanHistory({ page, limit });
			}, 15000);
		} else if (!hasQueuedJobs && intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [scanJobs]);

	const columns = [
		{
			title: 'File Name',
			dataIndex: ['asset', 'file_name'],
			key: 'file_name',
			render: (text) => text || '-'
		},
		{
			title: 'Upload Date',
			dataIndex: 'created_at',
			key: 'created_at',
			render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm')
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: (status) => {
				const config = STATUS_CONFIG[status] || { color: 'default', label: status };
				return <Tag color={config.color}>{config.label}</Tag>;
			}
		},
		{
			title: 'Action',
			key: 'action',
			render: (_, record) => {
				if (record.status === 'completed' && record.expense) {
					return (
						<a onClick={() => history.push(`/expenses?id=${record.expense._id}`)}>
							View Expense
						</a>
					);
				}
				if (record.status === 'failed') {
					return <span style={{ color: '#999' }}>{record.error_message || 'Processing failed'}</span>;
				}
				if (record.status === 'queued') {
					return <span style={{ color: '#999' }}>Processing...</span>;
				}
				return '-';
			}
		}
	];

	const handleTableChange = (pagination) => {
		getScanHistory({ page: pagination.current, limit: pagination.pageSize });
	};

	return (
		<div style={{ padding: '24px' }}>
			<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
				<Title level={4} style={{ margin: 0 }}>Scan History</Title>
				<Button type="primary" onClick={() => history.push('/scan-receipt')}>
					Scan Receipt
				</Button>
			</div>
			<Table
				columns={columns}
				dataSource={scanJobs}
				rowKey="_id"
				loading={loading}
				pagination={{
					current: page,
					pageSize: limit,
					total,
					showSizeChanger: false
				}}
				onChange={handleTableChange}
			/>
		</div>
	);
};

export default ScanHistoryPage;
